import { onCategoriesChanged } from 'owa-calendar-compose-form-actions';
import { getComposeEvent } from 'owa-calendar-compose-store-manager';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { updateCalendarEvent } from 'owa-calendar-events-actions';
import type CategoryType from 'owa-service/lib/contract/CategoryType';
import {
    lazyAddMasterCategories,
    lazyRemoveMasterCategories,
    lazyGetMasterCategoriesList,
} from 'owa-categories';
import { ApiError, ApiErrorCode } from 'owa-addins-core';
import { CategoryColor } from 'owa-addins-apis/lib/apis/categories/CategoryDetails';
import type { CalendarEntry, LocalCacheForRemoteCalendarEntry } from 'owa-graph-schema';
import { getCalendarEntryByFolderId } from 'owa-calendar-cache';
import { isSharedItem } from './sharedItemAdapter';
import { canEditCategories } from 'owa-calendar-event-capabilities';

export const addCategoriesItemCompose = () => (categoriesToBeAdded: string[]): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        const event = getComposeEvent(null /* formId */);
        let categoriesFromEvent: string[] = event.Categories;

        // adding of Categories is not supported on calendar items for permissions CanViewWhenBusy, CanViewTitlesAndLocations and CanViewAllDetails.
        // canEditCategories returns true for permissions Delegate and CanEdit.
        // canEditCategories returns false for permissions CanViewWhenBusy, CanViewTitlesAndLocations and CanViewAllDetails.
        if (
            (event != null && event.IsRecurring && event.CalendarItemType == 'Occurrence') ||
            !canEditCategories(event)
        ) {
            reject(new ApiError(ApiErrorCode.OperationNotSupported));
            return;
        }

        if (categoriesFromEvent.length > 0) {
            for (let category of categoriesFromEvent) {
                if (categoriesToBeAdded.indexOf(category) < 0) {
                    categoriesToBeAdded.push(category);
                }
            }
        }

        onCategoriesChanged(categoriesToBeAdded);
        resolve();
    });
};

export const removeCategoriesItemCompose = () => (
    categoriesToBeRemoved: string[]
): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        const event = getComposeEvent(null /* formId */);
        let categoriesFromEvent: string[] = event.Categories;
        let categoriesToBeAdded: string[] = [];

        // removing of Categories is not supported on calendar items for permissions CanViewWhenBusy, CanViewTitlesAndLocations and CanViewAllDetails.
        // canEditCategories returns true for permissions Delegate and CanEdit.
        // canEditCategories returns false for permissions CanViewWhenBusy, CanViewTitlesAndLocations and CanViewAllDetails.
        if (
            (event != null && event.IsRecurring && event.CalendarItemType == 'Occurrence') ||
            !canEditCategories(event)
        ) {
            reject(new ApiError(ApiErrorCode.OperationNotSupported));
            return;
        }

        for (let category of categoriesFromEvent) {
            let matchingCategories = categoriesToBeRemoved.filter(
                existingCategory => existingCategory === category
            );
            if (matchingCategories.length === 0) {
                categoriesToBeAdded.push(category);
            }
        }

        onCategoriesChanged(categoriesToBeAdded);
        resolve();
    });
};

export const getCategoriesItem = (event: CalendarEvent) => (): Promise<CategoryType[]> => {
    return getCategoriesItemUtil(event);
};

export async function addCategoriesMailbox(categoryList: CategoryType[]): Promise<boolean> {
    const addMasterCategories = await lazyAddMasterCategories.import();
    return addMasterCategories(categoryList, 'Addin');
}

export async function removeCategoriesMailbox(categoryList: string[]): Promise<boolean> {
    const removeMasterCategories = await lazyRemoveMasterCategories.import();
    return removeMasterCategories(categoryList, 'Addin');
}

export const getCategoriesMailbox = (event: CalendarEvent) => (): Promise<CategoryType[]> => {
    return getCategoriesMailboxUtil(event);
};

export async function getCategoriesItemUtil(event: CalendarEvent): Promise<CategoryType[]> {
    let categoriesOnItem: CategoryType[] = [];
    let categoriesFromEvent: string[] = event.Categories;

    let getMasterCategoriesList = await lazyGetMasterCategoriesList.import();
    const masterCategoryList: CategoryType[] = getMasterCategoriesList();

    for (let category of categoriesFromEvent) {
        let matchingCategories = masterCategoryList.filter(
            existingCategory => existingCategory.Name === category
        );
        let categorydetailsobject: CategoryType = {
            Name: category,
            Color: matchingCategories.length > 0 ? matchingCategories[0].Color : CategoryColor.None,
        };
        categoriesOnItem.push(categorydetailsobject);
    }
    return categoriesOnItem;
}

export async function getCategoriesMailboxUtil(event: CalendarEvent): Promise<CategoryType[]> {
    let getMasterCategoriesList = await lazyGetMasterCategoriesList.import();
    var masterCategoryList: CategoryType[] = getMasterCategoriesList();

    var isSharedItemFlag: boolean = isSharedItem(event)();

    // isSharedItemFlag will be true for any shared calendar item irrespective of the permission level.
    // For the permissions of CanEdit, CanViewAllDetails and Delegate permissions masterCategoryList is that of the delegator's categories.
    // For the permissions of CanViewWhenBusy, CanViewTitlesAndLocations masterCategoryList is that of the delegatee's categories.
    if (isSharedItemFlag) {
        let calendarEntry: CalendarEntry = getCalendarEntryByFolderId(event.ParentFolderId.Id);
        if ((calendarEntry as LocalCacheForRemoteCalendarEntry)?.RemoteCategories) {
            // on remote calendars, we use the remote category list
            masterCategoryList = (calendarEntry as LocalCacheForRemoteCalendarEntry)
                .RemoteCategories.MasterList;
        }
    }
    return masterCategoryList;
}

export const addCategoriesItemRead = (event: CalendarEvent) => (
    categoriesToBeAdded: string[]
): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        let categoriesFromEvent: string[] = event.Categories;

        // adding of Categories is not supported on shared calendar items for permissions CanViewWhenBusy, CanViewTitlesAndLocations and CanViewAllDetails.
        // canEditCategories returns true for permissions Delegate and CanEdit.
        // canEditCategories returns false for permissions CanViewWhenBusy, CanViewTitlesAndLocations and CanViewAllDetails.
        if (
            (event != null && event.IsRecurring && event.CalendarItemType == 'Occurrence') ||
            !canEditCategories(event)
        ) {
            reject(new ApiError(ApiErrorCode.OperationNotSupported));
            return;
        }

        if (categoriesFromEvent.length > 0) {
            for (let category of categoriesFromEvent) {
                if (categoriesToBeAdded.indexOf(category) < 0) {
                    categoriesToBeAdded.push(category);
                }
            }
        }

        updateCalendarEvent(
            event,
            {
                Categories: categoriesToBeAdded,
            } as CalendarEvent,
            'Addin'
        );
        resolve();
    });
};

export const removeCategoriesItemRead = (event: CalendarEvent) => (
    categoriesToBeRemoved: string[]
): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        let categoriesFromEvent: string[] = event.Categories;
        let categoriesToBeAdded: string[] = [];

        // removing of Categories is not supported on shared calendar items for permissions CanViewWhenBusy, CanViewTitlesAndLocations and CanViewAllDetails.
        // canEditCategories returns true for permissions Delegate and CanEdit.
        // canEditCategories returns false for permissions CanViewWhenBusy, CanViewTitlesAndLocations and CanViewAllDetails.
        if (
            (event != null && event.IsRecurring && event.CalendarItemType == 'Occurrence') ||
            !canEditCategories(event)
        ) {
            reject(new ApiError(ApiErrorCode.OperationNotSupported));
            return;
        }

        for (let category of categoriesFromEvent) {
            let matchingCategories = categoriesToBeRemoved.filter(
                existingCategory => existingCategory === category
            );
            if (matchingCategories.length === 0) {
                categoriesToBeAdded.push(category);
            }
        }

        updateCalendarEvent(
            event,
            {
                Categories: categoriesToBeAdded,
            } as CalendarEvent,
            'Addin'
        );
        resolve();
    });
};
